import React, { useState } from 'react';
import axios from 'axios';
import { Vendor, Product } from './types';
import config from '../../config';

interface ProductMappingTableProps {
  vendor: Vendor;
  onRefresh: () => void;
  readOnly?: boolean;
}

const ProductMappingTable: React.FC<ProductMappingTableProps> = ({
  vendor,
  onRefresh,
  readOnly = false
}) => {
  const [products, setProducts] = useState<Product[]>(vendor.products);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const baseURL = config.API_BASE_URL;

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setError(null);
    setSuccess(null);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setProducts(vendor.products); // Reset to original data
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      await axios.put(
        `${baseURL}/vendors/${vendor._id}/products`,
        { products },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess('Products updated successfully');
      setEditingIndex(null);
      onRefresh();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update products');
      console.error('Error updating products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProductChange = (index: number, field: keyof Product, value: string) => {
    setProducts(prev => prev.map((product, i) => 
      i === index ? { ...product, [field]: value } : product
    ));
  };

  const handleAddProduct = () => {
    setProducts(prev => [
      ...prev,
      {
        materialDesc: '',
        materialId: '',
        vendorProductName: ''
      }
    ]);
    setEditingIndex(products.length);
  };

  const handleDeleteProduct = (index: number) => {
    if (window.confirm('Are you sure you want to delete this product mapping?')) {
      setProducts(prev => prev.filter((_, i) => i !== index));
    }
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  React.useEffect(() => {
    if (error || success) {
      const timer = setTimeout(clearMessages, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  if (products.length === 0 && readOnly) {
    return (
      <div className="empty-products-readonly">
        <h4>No Product Mappings</h4>
        <p>This vendor doesn't have any product mappings configured yet.</p>
      </div>
    );
  }

  return (
    <div className="product-mapping-table">
      {/* Messages */}
      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={clearMessages} className="alert-close">✕</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={clearMessages} className="alert-close">✕</button>
        </div>
      )}

      {/* Action buttons */}
      {!readOnly && (
        <div className="table-actions">
          <button
            className="btn btn-sm btn-primary"
            onClick={handleAddProduct}
            disabled={editingIndex !== null}
          >
            + Add Product
          </button>
          {editingIndex !== null && (
            <>
              <button
                className="btn btn-sm btn-success"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={handleCancelEdit}
                disabled={loading}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      )}

      {/* Table */}
      <div className="table-container">
        <table className="products-table">
          <thead>
            <tr>
              <th>Material Description</th>
              <th>Material ID</th>
              <th>Vendor Product Name</th>
              {!readOnly && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {products.map((product, index) => (
              <tr key={index} className={editingIndex === index ? 'editing' : ''}>
                <td>
                  {editingIndex === index ? (
                    <input
                      type="text"
                      value={product.materialDesc}
                      onChange={(e) => handleProductChange(index, 'materialDesc', e.target.value)}
                      placeholder="Client's product name"
                      className="table-input"
                    />
                  ) : (
                    <span>{product.materialDesc || '-'}</span>
                  )}
                </td>
                <td>
                  {editingIndex === index ? (
                    <input
                      type="text"
                      value={product.materialId}
                      onChange={(e) => handleProductChange(index, 'materialId', e.target.value)}
                      placeholder="Product ID"
                      className="table-input"
                    />
                  ) : (
                    <code className="material-id">{product.materialId || '-'}</code>
                  )}
                </td>
                <td>
                  {editingIndex === index ? (
                    <input
                      type="text"
                      value={product.vendorProductName}
                      onChange={(e) => handleProductChange(index, 'vendorProductName', e.target.value)}
                      placeholder="Vendor's product name"
                      className="table-input"
                    />
                  ) : (
                    <span>{product.vendorProductName || '-'}</span>
                  )}
                </td>
                {!readOnly && (
                  <td>
                    <div className="action-buttons">
                      {editingIndex === index ? (
                        <>
                          <button
                            className="btn btn-xs btn-success"
                            onClick={handleSave}
                            disabled={loading}
                            title="Save"
                          >
                            ✓
                          </button>
                          <button
                            className="btn btn-xs btn-secondary"
                            onClick={handleCancelEdit}
                            disabled={loading}
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn btn-xs btn-warning"
                            onClick={() => handleEdit(index)}
                            disabled={editingIndex !== null}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn btn-xs btn-danger"
                            onClick={() => handleDeleteProduct(index)}
                            disabled={editingIndex !== null}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {products.length === 0 && !readOnly && (
          <div className="empty-table">
            <p>No products added yet. Click "Add Product" to start creating mappings.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductMappingTable;
